<script>
import WidgetHead from './WidgetHead.vue';
import WidgetBody from './WidgetBody.vue';
import WidgetFooter from './WidgetFooter.vue';
import InputRadioGroup from 'dashboard/routes/dashboard/settings/inbox/components/InputRadioGroup.vue';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';
import { mapGetters } from 'vuex';

export default {
  name: 'Widget',
  components: {
    WidgetHead,
    WidgetBody,
    WidgetFooter,
    InputRadioGroup,
  },
  mixins: [globalConfigMixin],
  props: {
    welcomeHeading: {
      type: String,
      default: '',
    },
    welcomeTagline: {
      type: String,
      default: '',
    },
    websiteName: {
      type: String,
      required: true,
    },
    logo: {
      type: String,
      default: '',
    },
    isOnline: {
      type: Boolean,
      default: true,
    },
    replyTime: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: '',
    },
    widgetBubblePosition: {
      type: String,
      default: '',
    },
    launcherStyle: {
      type: String,
      default: '',
    },
    launcherLabel: {
      type: String,
      default: '',
    },
    launcherProfileName: {
      type: String,
      default: '',
    },
    launcherProfileTagline: {
      type: String,
      default: '',
    },
    previewViewport: {
      type: String,
      default: 'mobile',
    },
  },
  data() {
    return {
      widgetScreens: [
        {
          id: 'default',
          title: this.$t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_SCREEN.DEFAULT'),
          checked: true,
        },
        {
          id: 'chat',
          title: this.$t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_SCREEN.CHAT'),
          checked: false,
        },
      ],
      isDefaultScreen: true,
      isWidgetVisible: false,
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    getWidgetConfig() {
      return {
        welcomeHeading: this.welcomeHeading,
        welcomeTagline: this.welcomeTagline,
        websiteName: this.websiteName,
        logo: this.logo,
        isDefaultScreen: this.isDefaultScreen,
        isOnline: this.isOnline,
        replyTime: this.replyTimeText,
        color: this.color,
      };
    },
    replyTimeText() {
      switch (this.replyTime) {
        case 'in_a_few_minutes':
          return this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.REPLY_TIME.IN_A_FEW_MINUTES'
          );
        case 'in_a_day':
          return this.$t('INBOX_MGMT.WIDGET_BUILDER.REPLY_TIME.IN_A_DAY');
        default:
          return this.$t('INBOX_MGMT.WIDGET_BUILDER.REPLY_TIME.IN_A_FEW_HOURS');
      }
    },
    bubblePositionClass() {
      return this.widgetBubblePosition === 'left'
        ? 'justify-start'
        : 'justify-end';
    },
    widgetPositionClass() {
      return this.widgetBubblePosition === 'left' ? 'self-start' : 'self-end';
    },
    isTextBubble() {
      return this.launcherStyle === 'branded_pill';
    },
    isProfileBadge() {
      return this.launcherStyle === 'profile_badge';
    },
  },
  methods: {
    handleScreenChange(item) {
      this.isDefaultScreen = item.id === 'default';
    },
    toggleWidget() {
      this.isWidgetVisible = !this.isWidgetVisible;
      this.isDefaultScreen = true;
    },
  },
};
</script>

<template>
  <div
    class="relative flex h-full min-h-[34rem] w-full flex-col justify-end"
    :class="previewViewport === 'desktop' ? 'px-5' : 'px-0'"
  >
    <div
      v-if="!isWidgetVisible"
      class="absolute inset-x-5 top-14 text-n-slate-12"
    >
      <div class="mb-8 flex items-center justify-between text-sm font-semibold">
        <span>{{ websiteName }}</span>
        <span class="h-4 w-5 rounded bg-n-slate-6" />
      </div>
      <div class="h-5 w-3/4 rounded bg-n-slate-7" />
      <div class="mt-3 h-5 w-2/3 rounded bg-n-slate-6" />
      <div
        class="mt-8 h-10 w-28 rounded-lg"
        :class="color ? 'bg-n-brand' : 'bg-n-slate-8'"
      />
    </div>
    <div
      v-if="isWidgetVisible"
      class="flex flex-col items-center"
      :class="widgetPositionClass"
    >
      <InputRadioGroup
        name="widget-screen"
        :items="widgetScreens"
        :action="handleScreenChange"
      />
    </div>
    <div
      v-if="isWidgetVisible"
      class="widget-wrapper flex h-[31.25rem] w-80 flex-col justify-between rounded-lg bg-n-slate-2 shadow-md dark:bg-n-solid-1"
      :class="widgetPositionClass"
    >
      <WidgetHead :config="getWidgetConfig" />
      <div>
        <WidgetBody
          v-if="!getWidgetConfig.isDefaultScreen"
          :config="getWidgetConfig"
        />
        <WidgetFooter :config="getWidgetConfig" />
        <div class="py-2.5 flex justify-center">
          <a
            class="items-center gap-0.5 text-n-slate-11 cursor-pointer flex filter grayscale opacity-90 hover:grayscale-0 hover:opacity-100 text-xxs"
          >
            <img
              class="max-w-2.5 max-h-2.5"
              :src="globalConfig.logoThumbnail"
            />
            <span>
              {{
                useInstallationName(
                  $t('INBOX_MGMT.WIDGET_BUILDER.BRANDING_TEXT'),
                  globalConfig.installationName
                )
              }}
            </span>
          </a>
        </div>
      </div>
    </div>
    <div class="flex mt-4 w-full" :class="bubblePositionClass">
      <button
        v-if="!isWidgetVisible && isProfileBadge"
        class="flex w-36 cursor-pointer flex-col items-center"
        @click="toggleWidget"
      >
        <span
          class="relative z-10 flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-n-slate-4 shadow-lg"
        >
          <span
            class="flex h-full w-full items-center justify-center bg-n-brand"
          >
            <img
              src="~dashboard/assets/images/chat-round-dots.svg"
              alt=""
              draggable="false"
              class="h-7 w-7 brightness-0 invert"
            />
          </span>
        </span>
        <span
          class="-mt-3 w-full rounded-xl bg-n-alpha-1 px-3 pb-3 pt-5 text-center shadow-lg dark:bg-n-solid-1"
        >
          <span class="block truncate text-sm font-semibold text-n-slate-12">
            {{ launcherProfileName || websiteName }}
          </span>
          <span
            v-if="launcherProfileTagline"
            class="mt-0.5 block truncate text-xs text-n-slate-11"
          >
            {{ launcherProfileTagline }}
          </span>
        </span>
      </button>
      <button
        v-else
        class="relative flex items-center justify-center rounded-full cursor-pointer"
        :style="{ background: color }"
        :class="
          isTextBubble && !isWidgetVisible
            ? 'w-auto font-medium text-base text-white dark:text-white h-12 px-4'
            : 'w-16 h-16'
        "
        @click="toggleWidget"
      >
        <img
          v-if="!isWidgetVisible"
          src="~dashboard/assets/images/chat-round-dots.svg"
          alt=""
          draggable="false"
          class="w-6 h-6 mx-auto brightness-0 invert"
        />
        <div
          v-if="isTextBubble && !isWidgetVisible"
          class="ltr:pl-2.5 rtl:pr-2.5"
        >
          {{ launcherLabel || $t('INBOX_MGMT.WIDGET_BRAND_KIT.PREVIEW_LABEL') }}
        </div>
        <div v-if="isWidgetVisible" class="relative">
          <div class="absolute w-0.5 h-8 rotate-45 -translate-y-1/2 bg-white" />
          <div
            class="absolute w-0.5 h-8 -rotate-45 -translate-y-1/2 bg-white"
          />
        </div>
      </button>
    </div>
  </div>
</template>
